/**
 * SPEC.md §3, §4, §11 — the topbar's New menu, disabled since M0. Issue
 * #16 (M4.2) turns it into a real dropdown grouped by category, listing
 * every `DiagramType` in `diagramTypes.ts` (M4.1, issue #15) by its
 * `label`. Selecting one hands the entry's `template` to the caller, which
 * treats it exactly like a user replacing the source by hand — no special
 * "loaded from a template" state anywhere downstream.
 *
 * Self-contained, mirroring Splitter.tsx's shape: this component only
 * reports a selection, App.tsx owns what happens to the source.
 *
 * M5.1 (issue #21) extracted the open/outside-click/Escape wiring below
 * into `useMenu` so the Export and Copy menus don't duplicate it —
 * behavior here is unchanged.
 */
import { ChevronDown } from 'lucide-react';
import { DIAGRAM_TYPES, type Category, type DiagramType } from '../lib/diagramTypes';
import { useMenu } from './useMenu';

// Order matches SPEC.md §4's Category union declaration order.
const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'process', label: 'Process' },
  { id: 'interaction', label: 'Interaction' },
  { id: 'structure', label: 'Structure' },
  { id: 'planning', label: 'Planning' },
  { id: 'data', label: 'Data' },
];

interface NewMenuProps {
  onSelect: (type: DiagramType) => void;
}

export default function NewMenu({ onSelect }: NewMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();

  function handleSelect(type: DiagramType) {
    onSelect(type);
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        New
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="New diagram"
          className="absolute left-0 top-full z-10 mt-1 max-h-[70vh] w-56 overflow-y-auto rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {CATEGORIES.map((category) => {
            const types = DIAGRAM_TYPES.filter((type) => type.category === category.id);
            if (types.length === 0) return null;
            return (
              <div key={category.id} role="group" aria-label={category.label}>
                <div className="pane-label px-3 py-1.5 text-mute">{category.label}</div>
                {types.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    role="menuitem"
                    onClick={() => handleSelect(type)}
                    className="block w-full px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
